import { useMutation } from "@apollo/client";
import { useRef, useState } from "react";
import { Check as CheckIcon } from "@tamagui/lucide-icons";
import { appear, Badge, Body3, Divider, Input, type InputProps, XStack, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { normalizeMemberId } from "@medbillai/utils/memberId";
import { type OnboardingNavProps, OnboardingScreen } from "../../components/OnboardingScreen";
import { captureUnreportedError } from "../../lib/apollo/error";
import { useBoolVariation } from "../../lib/flag";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { MemberCardScan } from "./MemberCardScan";
import type { OnboardingIdentity } from "./types";
import { ingestMemberCardMutation } from "./utils/queries";
import { useRecordCarrier } from "./utils/useRecordCarrier";
export type MemberIdOutcomes = {
  submitted: {};
  /** The user doesn't have the ID to hand; the carrier is recorded without it. */
  skipped: {};
  back: {};
};
const Outcome = tagged<MemberIdOutcomes>();
const V3_NAV: OnboardingNavProps = {
  showLogo: false
};
export function MemberId({
  identity,
  nav = V3_NAV,
  carrier,
  payerId,
  onDone
}: {
  identity?: OnboardingIdentity;
  nav?: OnboardingNavProps;
  carrier: string;
  payerId: string;
  onDone: OnDone<MemberIdOutcomes>;
}) {
  const [typed, setTyped] = useState("");
  const [rejected, setRejected] = useState(false);
  const [readId, setReadId] = useState<string>();
  const cardUpload = useRef<Promise<string | undefined> | undefined>(undefined);
  const [ingestMemberCard] = useMutation(ingestMemberCardMutation);
  const readsCard = useBoolVariation(Flag.INSURANCE_CARD_MEMBER_ID_READ, false);
  const latest = useRef({
    typed,
    readId
  });
  latest.current = {
    typed,
    readId
  };
  // Blocks a second tap from landing a second record.
  const [answered, setAnswered] = useState(false);
  const recordCarrier = useRecordCarrier(identity);
  const record = (memberId?: string) => {
    setAnswered(true);
    const card = cardUpload.current;
    void recordCarrier(carrier, payerId, memberId)
    // After the confirmed ID is recorded, so the card's row never lands first.
    .then(() => card).then(documentId => documentId === undefined ? undefined : ingestMemberCard({
      variables: {
        documentId,
        memberId
      }
    })).catch(captureUnreportedError).finally(() => setAnswered(false));
  };
  const showsCardBadge = readId !== undefined && typed === readId;
  const submit = () => {
    const memberId = normalizeMemberId(typed);
    if (memberId === null) {
      setRejected(true);
      return;
    }
    onDone(Outcome.submitted());
    record(memberId);
  };
  const skip = () => {
    onDone(Outcome.skipped());
    record();
  };
  return <OnboardingScreen contained data-sentry-element="OnboardingScreen" data-sentry-component="MemberId" data-sentry-source-file="MemberId.tsx">
      <OnboardingScreen.Nav {...nav} onBackPress={() => onDone(Outcome.back())} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="MemberId.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="MemberId.tsx">
        <OnboardingScreen.Header alignment="left" title="What's your member ID?" subtitle="It's on the front of your insurance card. We use it to look up your plan." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="MemberId.tsx" />
        <MemberIdInput testID="member-id.input" value={typed} onChangeText={value => {
        setTyped(value);
        setRejected(false);
      }} rejected={rejected} prominent={readsCard} autoFocus suffix={showsCardBadge ? <FromCard /> : null} data-sentry-element="MemberIdInput" data-sentry-source-file="MemberId.tsx" />
        {readsCard ? <>
            <OrDivider />
            <MemberCardScan onUpload={storedDocumentId => {
          cardUpload.current = storedDocumentId;
        }} onRead={memberId => {
          const now = latest.current;
          const untouchedSinceRead = now.typed.trim() === "" || now.typed === now.readId;
          if (memberId !== null && untouchedSinceRead) {
            setTyped(memberId);
            setReadId(memberId);
            setRejected(false);
          }
        }} onRemove={() => {
          if (typed === readId) {
            setTyped("");
          }
          setReadId(undefined);
          cardUpload.current = undefined;
        }} />
          </> : null}
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="MemberId.tsx">
        <OnboardingScreen.PrimaryAction testID="member-id.continue" variant="brand" disabled={typed.trim() === "" || answered} onPress={submit} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="MemberId.tsx">
          Continue
        </OnboardingScreen.PrimaryAction>
        <OnboardingScreen.TertiaryAction testID="member-id.skip" disabled={answered} onPress={skip} data-sentry-element="OnboardingScreen.TertiaryAction" data-sentry-source-file="MemberId.tsx">
          {readsCard ? "I don't have my card right now" : "Skip"}
        </OnboardingScreen.TertiaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}
export function MemberIdInput({
  rejected,
  ...props
}: Pick<InputProps, "testID" | "value" | "onChangeText" | "autoFocus" | "suffix" | "prominent"> & {
  rejected: boolean;
}) {
  return <Input {...props} label="Member ID" autoCapitalize="characters" autoComplete="off" autoCorrect={false} spellCheck={false} validation={rejected ? "error" : "none"} helperText={rejected ? "That doesn't look like a member ID. Check your card and try again." : undefined} data-sentry-element="Input" data-sentry-component="MemberIdInput" data-sentry-source-file="MemberId.tsx" />;
}
function FromCard() {
  return <YStack {...appear("fade")} data-sentry-element="YStack" data-sentry-component="FromCard" data-sentry-source-file="MemberId.tsx">
      <Badge status="positive" size="small" iconLeft={CheckIcon} data-sentry-element="Badge" data-sentry-source-file="MemberId.tsx">
        From your card
      </Badge>
    </YStack>;
}
function OrDivider() {
  return <XStack alignItems="center" gap="$sp1.5" paddingVertical="$sp2" data-sentry-element="XStack" data-sentry-component="OrDivider" data-sentry-source-file="MemberId.tsx">
      <Divider flex={1} alignSelf="center" data-sentry-element="Divider" data-sentry-source-file="MemberId.tsx" />
      <Body3 color="$textSubtle" data-sentry-element="Body3" data-sentry-source-file="MemberId.tsx">or</Body3>
      <Divider flex={1} alignSelf="center" data-sentry-element="Divider" data-sentry-source-file="MemberId.tsx" />
    </XStack>;
}